import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  CheckCircle2, 
  XCircle, 
  Shield, 
  User, 
  Mail, 
  Key, 
  ArrowRight,
  ExternalLink,
  LogOut,
  Lock,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DemoAppPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [protectedData, setProtectedData] = useState(null);
  const [error, setError] = useState(null);

  // Check for error in URL
  const urlError = searchParams.get('error');

  // Fetch session on mount
  useEffect(() => {
    fetchSession();
  }, []);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/demo-app/session`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSession(data);
    } catch (err) {
      console.error('Session fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const startSSOLogin = () => {
    // Redirect to SSO start with return_to pointing back here
    window.location.href = `${API_URL}/api/demo-app/sso/start?return_to=/demo-app`;
  };

  const testProtectedEndpoint = async () => {
    try {
      const response = await fetch(`${API_URL}/api/demo-app/protected-data`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProtectedData(data);
        setError(null);
      } else {
        setError('Not authenticated - 401 Unauthorized');
        setProtectedData(null);
      }
    } catch (err) {
      setError('Request failed');
      setProtectedData(null);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/demo-app/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setSession({ authenticated: false, user: null });
      setProtectedData(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-4">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">OIDC SSO Demo</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Demo Application</h1>
          <p className="text-slate-400 text-lg">
            Test the OIDC Single Sign-On flow with GETGRIDGATE as the Identity Provider
          </p>
        </div>

        {/* Error Banner */}
        {urlError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-medium">Authentication Error</p>
              <p className="text-red-300/70 text-sm">{urlError.replace(/_/g, ' ')}</p>
            </div>
          </div>
        )}

        {/* Session Status Card */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${session?.authenticated ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                  {session?.authenticated ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                  ) : (
                    <Lock className="w-6 h-6 text-amber-400" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-white">Session Status</CardTitle>
                  <CardDescription className="text-slate-400">
                    {session?.authenticated ? 'Authenticated via GETGRIDGATE SSO' : 'Not authenticated'}
                  </CardDescription>
                </div>
              </div>
              <Badge 
                variant={session?.authenticated ? 'default' : 'secondary'}
                className={session?.authenticated 
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                  : 'bg-slate-700 text-slate-300'}
              >
                {session?.authenticated ? 'Active Session' : 'No Session'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {session?.authenticated && session?.user ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <User className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Name</p>
                      <p className="text-white font-medium">{session.user.name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Mail className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                      <p className="text-white font-medium">{session.user.email || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Key className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">User ID</p>
                      <p className="text-white font-mono text-sm">{session.user.id || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Shield className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Roles</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {session.user.roles?.length > 0 ? (
                          session.user.roles.map((role, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-cyan-500/30 text-cyan-400">
                              {role}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-slate-400 text-sm">No roles</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator className="bg-slate-700" />

                <div className="flex gap-3">
                  <Button 
                    onClick={handleLogout}
                    variant="outline" 
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                  <Button 
                    onClick={fetchSession}
                    variant="outline" 
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Session
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-slate-400 mb-4">
                  Click below to initiate the OIDC SSO login flow
                </p>
                <Button 
                  onClick={startSSOLogin}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  size="lg"
                  data-testid="demo-app-sso-login-btn"
                >
                  Login with GETGRIDGATE
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Protected API Test Card */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-400" />
              Protected API Test
            </CardTitle>
            <CardDescription className="text-slate-400">
              Test accessing a protected endpoint that requires authentication
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={testProtectedEndpoint}
              variant="outline"
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              data-testid="test-protected-api-btn"
            >
              Call /api/demo-app/protected-data
            </Button>

            {protectedData && (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Success!
                </p>
                <pre className="text-slate-300 text-sm overflow-auto">
                  {JSON.stringify(protectedData, null, 2)}
                </pre>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 font-medium flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {error}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SSO Flow Diagram */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">OIDC SSO Flow</CardTitle>
            <CardDescription className="text-slate-400">
              How the authentication flow works
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">1</div>
                <div>
                  <p className="text-white font-medium">User clicks "Login with GETGRIDGATE"</p>
                  <p className="text-slate-400 text-sm">App redirects to <code className="text-cyan-400">/api/demo-app/sso/start</code></p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">2</div>
                <div>
                  <p className="text-white font-medium">Server generates PKCE & state</p>
                  <p className="text-slate-400 text-sm">Redirects to GETGRIDGATE <code className="text-cyan-400">/api/oauth/authorize</code></p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">3</div>
                <div>
                  <p className="text-white font-medium">User authenticates at GETGRIDGATE</p>
                  <p className="text-slate-400 text-sm">If already logged in, automatic redirect; otherwise, show login form</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">4</div>
                <div>
                  <p className="text-white font-medium">GETGRIDGATE redirects with auth code</p>
                  <p className="text-slate-400 text-sm">To <code className="text-cyan-400">/api/demo-app/auth/callback?code=...&state=...</code></p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">5</div>
                <div>
                  <p className="text-white font-medium">Server exchanges code for tokens</p>
                  <p className="text-slate-400 text-sm">Validates state, exchanges code via <code className="text-cyan-400">/api/oauth/token</code></p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">6</div>
                <div>
                  <p className="text-white font-medium">Session created, user redirected</p>
                  <p className="text-slate-400 text-sm">Session cookie set, redirected to original <code className="text-cyan-400">return_to</code> path</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to Hub Link */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Back to GETGRIDGATE Hub
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DemoAppPage;
